/**
 * @module IdleTimeoutModal
 *
 * Animated warning modal that appears when the user has been idle.
 * Displays a circular SVG countdown timer and gives the user the
 * option to stay signed in or sign out immediately.
 *
 * Design decisions:
 *   - Amber/warm tones (restrained color strategy): urgent but not alarming
 *   - Shield icon: communicates security context
 *   - Circular SVG countdown: visually intuitive time remaining
 *   - Framer-motion for entrance/exit: scale + opacity transitions
 *   - Focus trap + aria-modal for accessibility
 *   - prefers-reduced-motion respected
 */

import { useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, LogOut } from "lucide-react";

// ─── Types ───────────────────────────────────────────────────

interface IdleTimeoutModalProps {
  /** Whether the modal is currently visible */
  isOpen: boolean;
  /** Seconds remaining before auto-logout */
  remainingSeconds: number;
  /** Total seconds for the countdown (used to calculate progress ring) */
  totalSeconds: number;
  /** Called when user clicks "Stay Signed In" */
  onStaySignedIn: () => void;
  /** Called when user clicks "Sign Out Now" */
  onSignOut: () => void;
}

// ─── Animation Variants ──────────────────────────────────────

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: -8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "tween" as const,
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1] as [number, number, number, number],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.97,
    transition: {
      type: "tween" as const,
      duration: 0.2,
      ease: [0.4, 0, 1, 1] as [number, number, number, number],
    },
  },
};

// ─── Component ───────────────────────────────────────────────

export function IdleTimeoutModal({
  isOpen,
  remainingSeconds,
  totalSeconds,
  onStaySignedIn,
  onSignOut,
}: IdleTimeoutModalProps) {
  const stayButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // ── Focus Management ──────────────────────────────────────

  useEffect(() => {
    if (isOpen) {
      // Focus the "Stay Signed In" button when modal opens
      const timer = setTimeout(() => {
        stayButtonRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // ── Keyboard Handler ──────────────────────────────────────

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === "Escape" || e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        onStaySignedIn();
      }

      // Focus trap: keep Tab within the modal
      if (e.key === "Tab" && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    },
    [isOpen, onStaySignedIn]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown, true);
      // Prevent background scroll
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", handleKeyDown, true);
        document.body.style.overflow = "";
      };
    }
  }, [isOpen, handleKeyDown]);

  // ── SVG Countdown Ring ────────────────────────────────────

  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const progress = totalSeconds > 0 ? remainingSeconds / totalSeconds : 0;
  const strokeDashoffset = circumference * (1 - progress);
  const isUrgent = remainingSeconds <= 10;

  // ── Ring Color (transitions from amber to warm red) ───────

  const getRingColor = () => {
    if (remainingSeconds <= 5) return "#ef4444"; // Red for final 5s
    if (remainingSeconds <= 10) return "#f97316"; // Orange for 6-10s
    return "#f59e0b"; // Amber for 11-60s
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="idle-timeout-backdrop"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.3 }}
          aria-hidden="true"
        >
          <motion.div
            ref={modalRef}
            className="idle-timeout-modal"
            role="alertdialog"
            aria-modal="true"
            aria-label="Session timeout warning"
            aria-describedby="idle-timeout-description"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* ── Countdown Ring ── */}
            <div className="idle-timeout-timer">
              <svg
                className="idle-timeout-ring"
                viewBox="0 0 120 120"
                aria-hidden="true"
              >
                {/* Background track */}
                <circle
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="none"
                  stroke="rgba(245, 158, 11, 0.12)"
                  strokeWidth="6"
                />
                {/* Animated progress ring */}
                <circle
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="none"
                  stroke={getRingColor()}
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  transform="rotate(-90 60 60)"
                  style={{
                    transition: "stroke-dashoffset 0.25s linear, stroke 0.5s ease",
                  }}
                />
              </svg>

              {/* Center content: icon + seconds */}
              <div className="idle-timeout-timer-content">
                <motion.div
                  className="idle-timeout-shield"
                  animate={
                    isUrgent
                      ? {
                          scale: [1, 1.08, 1],
                        }
                      : {}
                  }
                  transition={
                    isUrgent
                      ? {
                          duration: 1.2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }
                      : {}
                  }
                >
                  <Shield
                    size={28}
                    style={{
                      color: getRingColor(),
                      transition: "color 0.5s ease",
                    }}
                  />
                </motion.div>
                <span
                  className="idle-timeout-seconds"
                  style={{ color: getRingColor() }}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {remainingSeconds}
                </span>
                <span className="idle-timeout-seconds-label">seconds</span>
              </div>
            </div>

            {/* ── Text ── */}
            <div className="idle-timeout-text">
              <h2 className="idle-timeout-title">Session Timeout</h2>
              <p id="idle-timeout-description" className="idle-timeout-description">
                Your session will expire due to inactivity.
                <br />
                Any unsaved changes may be lost.
              </p>
            </div>

            {/* ── Actions ── */}
            <div className="idle-timeout-actions">
              <button
                ref={stayButtonRef}
                type="button"
                className="idle-timeout-btn-stay"
                onClick={onStaySignedIn}
              >
                Stay Signed In
              </button>
              <button
                type="button"
                className="idle-timeout-btn-signout"
                onClick={onSignOut}
              >
                <LogOut size={16} />
                Sign Out Now
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
